import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CircularProgress from '@/components/Loading/CircularProgress'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import { extractName } from '@/modules/Drive/utils'
import XIcons from '@/icons/XIcons'

interface ProgressBarProps {
    percent: Record<string, number>
    onCancel?: (fileName: string) => void
    type?: 'upload' | 'download'
}

// Define file status types
type FileStatus = 'processing' | 'completed' | 'aborted'

interface FileProgressInfo {
    percent: number
    status: FileStatus
    timestamp: number // To track when to remove the file from display
}

const DISPLAY_DURATION = 5000 // ms

const ProgressBar: React.FC<ProgressBarProps> = ({
    percent,
    onCancel,
    type = 'upload',
}) => {
    const [fileStatuses, setFileStatuses] = useState<
        Record<string, FileProgressInfo>
    >({})
    const [isHovered, setIsHovered] = useState<string | null>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const title = useMemo(() => {
        return type === 'upload' ? 'upload-process' : 'download-process'
    }, [type])

    // Update fileStatuses when percent changes
    useEffect(() => {
        const now = Date.now()
        setFileStatuses((prev) => {
            const updated: Record<string, FileProgressInfo> = { ...prev }

            Object.entries(percent).forEach(([fileName, percentValue]) => {
                if (updated[fileName]?.status === 'aborted') return
                updated[fileName] =
                    percentValue >= 100
                        ? { percent: 100, status: 'completed', timestamp: now }
                        : {
                              percent: percentValue,
                              status: 'processing',
                              timestamp: now,
                          }
            })
            // Keep completed/aborted files if still within display window
            Object.entries(prev).forEach(([fileName, info]) => {
                if (
                    !percent[fileName] &&
                    (info.status === 'completed' ||
                        info.status === 'aborted') &&
                    now - info.timestamp < DISPLAY_DURATION
                ) {
                    updated[fileName] = info
                }
            })
            return updated
        })
    }, [percent])

    // Cleanup old entries periodically
    useEffect(() => {
        if (timerRef.current) clearInterval(timerRef.current)
        timerRef.current = setInterval(() => {
            setFileStatuses((prev) => {
                const now = Date.now()
                const updated = { ...prev }
                let changed = false
                Object.entries(updated).forEach(([fileName, info]) => {
                    if (
                        (info.status === 'completed' ||
                            info.status === 'aborted') &&
                        now - info.timestamp > DISPLAY_DURATION
                    ) {
                        delete updated[fileName]
                        changed = true
                    }
                })
                return changed ? updated : prev
            })
        }, 1000)
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [])

    const handleCancel = useCallback(
        (fileName: string) => {
            if (onCancel) {
                onCancel(fileName)
                setFileStatuses((prev) => ({
                    ...prev,
                    [fileName]: {
                        percent: prev[fileName]?.percent || 0,
                        status: 'aborted',
                        timestamp: Date.now(),
                    },
                }))
            }
        },
        [onCancel]
    )

    const entries = useMemo(() => Object.entries(fileStatuses), [fileStatuses])

    if (entries.length === 0) return null

    return (
        <div className="w-96 rounded-2xl border bg-white/90 shadow-xl backdrop-blur p-4">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-800">
                    {t(title)}
                </h4>
                <span className="text-xs text-gray-500">{entries.length}</span>
            </div>
            <div className="space-y-4 mt-3">
                {entries.map(([fileName, info]) => (
                    <div
                        key={fileName}
                        className="group"
                        onMouseEnter={() => setIsHovered(fileName)}
                        onMouseLeave={() => setIsHovered(null)}
                    >
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="truncate max-w-[72%] text-sm text-gray-700">
                                {extractName(fileName)}
                            </div>
                            <div className="flex items-center gap-2">
                                {info.status === 'processing' && (
                                    <>
                                        <span className="text-xs text-gray-500 tabular-nums">
                                            {info.percent}%
                                        </span>
                                        {isHovered === fileName && onCancel ? (
                                            <button
                                                onClick={() =>
                                                    handleCancel(fileName)
                                                }
                                                className="rounded-md p-1 hover:bg-gray-100"
                                                title={t('cancel')}
                                                aria-label={`${t('cancel')} ${fileName}`}
                                            >
                                                <XIcons className="h-5 w-5 text-gray-500 hover:text-red-500" />
                                            </button>
                                        ) : (
                                            <CircularProgress
                                                percent={info.percent}
                                                className="h-6 w-6"
                                            />
                                        )}
                                    </>
                                )}
                                {info.status === 'completed' && (
                                    <CheckCircleIcons className="h-6 w-6 text-green-500" />
                                )}
                                {info.status === 'aborted' && (
                                    <span className="text-xs text-red-500">
                                        {t('abort-process')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className={
                                    info.status === 'aborted'
                                        ? 'h-full bg-red-300'
                                        : info.status === 'completed'
                                          ? 'h-full bg-green-500'
                                          : 'h-full bg-blue-500'
                                }
                                style={{
                                    width: `${Math.min(info.percent, 100)}%`,
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default ProgressBar
